// CB:BEGIN import-bids-modal
import React, { useEffect, useState } from 'react';
import { useApi } from '@/lib/useApi';
import { FaTimes, FaArrowRight } from 'react-icons/fa';

interface PlaceholderBid {
  id: string;
  projectId: string;
  amount: number | null;
  title: string | null;
  note: string | null;
  scopeNotes: string | null;
  createdAt: string;
  project: {
    id: string;
    address: string;
    status: string;
  };
  contractor: {
    id: string;
    company: string | null;
  } | null;
}

interface Contractor {
  id: string;
  company?: string;
  user: {
    email: string;
    firstName?: string;
    lastName?: string;
  };
}

interface ImportBidsModalProps {
  contractorId: string;
  contractorName: string;
  isOpen: boolean;
  onClose: () => void;
  onMigrated?: () => void;
}

export const ImportBidsModal: React.FC<ImportBidsModalProps> = ({
  contractorId,
  contractorName,
  isOpen,
  onClose,
  onMigrated,
}) => {
  const api = useApi();
  const [bids, setBids] = useState<PlaceholderBid[]>([]);
  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [loading, setLoading] = useState(false);
  const [migrating, setMigrating] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Track selected contractor for each bid
  const [selectedContractors, setSelectedContractors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isOpen) {
      fetchPlaceholderBids();
      fetchContractors();
    }
  }, [isOpen, contractorId]);

  const fetchPlaceholderBids = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get(`/api/admin/contractors/${contractorId}/placeholder-bids`);
      const bidsList = data.bids || [];
      setBids(bidsList);
      
      // Initialize selected contractors - default to the current contractor for each bid
      const initialSelections: Record<string, string> = {};
      bidsList.forEach((bid: PlaceholderBid) => {
        initialSelections[bid.id] = contractorId;
      });
      setSelectedContractors(initialSelections);
    } catch (err: any) {
      console.error('Error fetching placeholder bids:', err);
      setError(err?.message || 'Failed to load bids');
      setBids([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchContractors = async () => {
    try {
      const normalizeResponse = (data: any): Contractor[] => {
        if (!data) return [];
        if (Array.isArray(data)) return data;
        if (Array.isArray(data.contractors)) return data.contractors;
        return [];
      };

      try {
        const data = await api.get('/api/admin/contractors');
        setContractors(normalizeResponse(data));
      } catch (primaryErr) {
        console.warn('⚠️ /api/admin/contractors failed, trying dev endpoint...', primaryErr);
        try {
          const data = await api.get('/api/admin/contractors');
          setContractors(normalizeResponse(data));
        } catch (fallbackErr) {
          console.error('Error fetching contractors:', fallbackErr);
          setContractors([]);
        }
      }
    } catch (err) {
      console.error('Error fetching contractors:', err);
      setContractors([]);
    }
  };

  const handleMigrateBid = async (bidId: string) => {
    if (migrating) return;
    
    const targetContractorId = selectedContractors[bidId];
    if (!targetContractorId) {
      setError('Please select a contractor for this bid');
      return;
    }
    
    setMigrating(bidId);
    setError(null);
    
    try {
      await api.post(`/api/admin/bids/${bidId}/migrate`, {
        contractorId: targetContractorId,
      });
      
      // Remove migrated bid from list
      setBids((prev) => prev.filter((bid) => bid.id !== bidId));
      
      // Remove from selected contractors
      setSelectedContractors((prev) => {
        const updated = { ...prev };
        delete updated[bidId];
        return updated;
      });
      
      if (onMigrated) {
        onMigrated();
      }
    } catch (err: any) {
      console.error('Error migrating bid:', err);
      setError(err?.message || 'Failed to migrate bid');
    } finally {
      setMigrating(null);
    }
  };

  const getContractorDisplayName = (contractor: Contractor): string => {
    return contractor.company || 
           `${contractor.user.firstName || ''} ${contractor.user.lastName || ''}`.trim() || 
           contractor.user.email;
  };

  if (!isOpen) return null;

  // Extract contractor name from bid note (format: "Contractor: {name}")
  const getContractorNameFromBid = (bid: PlaceholderBid): string => {
    if (bid.note) {
      const match = bid.note.match(/^Contractor:\s*(.+?)(?:\n|$)/);
      if (match) {
        return match[1].trim();
      }
    }
    return 'Unknown Contractor';
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={() => !migrating && onClose()}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          width: '90%',
          maxWidth: '800px',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div>
            <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', margin: 0, marginBottom: '4px' }}>
              Import Bids for {contractorName}
            </h2>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
              Migrate manually entered bids to this contractor account
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={!!migrating}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: migrating ? 'not-allowed' : 'pointer',
              padding: '8px',
              borderRadius: '8px',
              color: '#6b7280',
            }}
            onMouseEnter={(e) => {
              if (!migrating) e.currentTarget.style.background = '#f3f4f6';
            }}
            onMouseLeave={(e) => {
              if (!migrating) e.currentTarget.style.background = 'transparent';
            }}
          >
            <FaTimes size={20} />
          </button>
        </div>

        {/* Error message */}
        {error && (
          <div
            style={{
              padding: '12px',
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              marginBottom: '16px',
              color: '#991b1b',
              fontSize: '14px',
            }}
          >
            {error}
          </div>
        )}

        {/* Loading state */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
            Loading bids...
          </div>
        )}

        {/* Bids list */}
        {!loading && bids.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
            <p style={{ fontSize: '16px', marginBottom: '8px' }}>No placeholder bids found</p>
            <p style={{ fontSize: '14px' }}>All bids have been migrated or no manual bids exist for this contractor name.</p>
          </div>
        )}

        {!loading && bids.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {bids.map((bid) => {
              const bidContractorName = getContractorNameFromBid(bid);
              const amount = bid.amount ? `$${(bid.amount / 100).toFixed(2)}` : 'N/A';
              
              return (
                <div
                  key={bid.id}
                  style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    padding: '16px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: '#f9fafb',
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                      <div style={{ fontSize: '16px', fontWeight: '600', color: '#111827' }}>
                        {bid.title || 'Untitled Bid'}
                      </div>
                      <div style={{ fontSize: '14px', color: '#6b7280' }}>
                        {amount}
                      </div>
                    </div>
                    <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>
                      <strong>Project:</strong> {bid.project.address}
                    </div>
                    <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>
                      <strong>Manual Contractor Name:</strong> {bidContractorName}
                    </div>
                    <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                      Created {new Date(bid.createdAt).toLocaleDateString()}
                    </div>
                    {/* CB:BEGIN contractor-selector-dropdown */}
                    <div style={{ marginTop: '12px' }}>
                      <label style={{ 
                        display: 'block', 
                        fontSize: '14px', 
                        fontWeight: '500', 
                        color: '#374151', 
                        marginBottom: '6px' 
                      }}>
                        Migrate to Contractor:
                      </label>
                      <select
                        value={selectedContractors[bid.id] || contractorId}
                        onChange={(e) => {
                          setSelectedContractors((prev) => ({
                            ...prev,
                            [bid.id]: e.target.value,
                          }));
                        }}
                        disabled={!!migrating}
                        style={{
                          width: '100%',
                          maxWidth: '400px',
                          padding: '8px 12px',
                          borderRadius: '6px',
                          border: '1px solid #d1d5db',
                          fontSize: '14px',
                          backgroundColor: migrating ? '#f3f4f6' : 'white',
                          cursor: migrating ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {contractors.map((contractor) => (
                          <option key={contractor.id} value={contractor.id}>
                            {getContractorDisplayName(contractor)}
                          </option>
                        ))}
                      </select>
                    </div>
                    {/* CB:END contractor-selector-dropdown */}
                  </div>
                  <button
                    onClick={() => handleMigrateBid(bid.id)}
                    disabled={!!migrating || !selectedContractors[bid.id]}
                    style={{
                      padding: '10px 20px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      border: 'none',
                      background: migrating === bid.id ? '#9ca3af' : '#10b981',
                      color: 'white',
                      cursor: migrating ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      whiteSpace: 'nowrap',
                    }}
                    onMouseEnter={(e) => {
                      if (migrating !== bid.id) {
                        e.currentTarget.style.background = '#059669';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (migrating !== bid.id) {
                        e.currentTarget.style.background = '#10b981';
                      }
                    }}
                  >
                    {migrating === bid.id ? (
                      'Migrating...'
                    ) : (
                      <>
                        Migrate Bid <FaArrowRight size={14} />
                      </>
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
// CB:END import-bids-modal

